import { useEffect, useMemo } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query'
import { useViewsStore } from '@/stores/views.store'
import * as viewsService from '@/services/views.service'
import type {
  ViewEntityType,
  ViewListItem,
  CreateView,
  UpdateView,
} from '@/types/api'

// Stable empty array to avoid creating new references
const EMPTY_VIEWS: ViewListItem[] = []

// =============================================================================
// Query Keys Factory
// =============================================================================

export const viewsKeys = {
  all: ['views'] as const,
  lists: () => [...viewsKeys.all, 'list'] as const,
  list: (entityType: ViewEntityType) =>
    [...viewsKeys.lists(), entityType] as const,
  details: () => [...viewsKeys.all, 'detail'] as const,
  detail: (id: string) => [...viewsKeys.details(), id] as const,
}

// =============================================================================
// Query Options
// =============================================================================

const VIEWS_STALE_TIME = 60000 // 1 minute
const VIEWS_GC_TIME = 600000 // 10 minutes

export const viewsQueryOptions = (entityType: ViewEntityType) =>
  queryOptions({
    queryKey: viewsKeys.list(entityType),
    queryFn: () => viewsService.getViews(entityType),
    staleTime: VIEWS_STALE_TIME,
    gcTime: VIEWS_GC_TIME,
  })

export const viewQueryOptions = (viewId: string | null) =>
  queryOptions({
    queryKey: viewsKeys.detail(viewId ?? ''),
    queryFn: () => (viewId ? viewsService.getView(viewId) : null),
    enabled: !!viewId,
    staleTime: VIEWS_STALE_TIME,
    gcTime: VIEWS_GC_TIME,
  })

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch and manage views for an entity type
 */
export function useViews(entityType: ViewEntityType) {
  const { setViews, setLoading } = useViewsStore()

  const query = useQuery(viewsQueryOptions(entityType))

  // Sync loading state with store
  useEffect(() => {
    setLoading(entityType, query.isLoading)
  }, [entityType, query.isLoading, setLoading])

  // Sync views data with store
  useEffect(() => {
    if (query.data) {
      setViews(entityType, query.data)
    }
  }, [entityType, query.data, setViews])

  // Use stable reference for views to prevent unnecessary re-renders
  const views = useMemo(() => query.data ?? EMPTY_VIEWS, [query.data])

  return {
    views,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a single view by ID
 */
export function useView(viewId: string | null) {
  return useQuery(viewQueryOptions(viewId))
}

/**
 * Hook to create a new view
 */
export function useCreateView(entityType: ViewEntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateView) => viewsService.createView(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: viewsKeys.list(entityType) })
    },
  })
}

/**
 * Hook to update an existing view
 */
export function useUpdateView(entityType: ViewEntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateView }) =>
      viewsService.updateView(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: viewsKeys.list(entityType) })
      queryClient.invalidateQueries({
        queryKey: viewsKeys.detail(variables.id),
      })
    },
  })
}

/**
 * Hook to delete a view
 */
export function useDeleteView(entityType: ViewEntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => viewsService.deleteView(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: viewsKeys.list(entityType) })
    },
  })
}

/**
 * Hook to duplicate an existing view
 */
export function useDuplicateView(entityType: ViewEntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sourceViewId,
      newName,
    }: {
      sourceViewId: string
      newName: string
    }) => viewsService.duplicateView(sourceViewId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: viewsKeys.list(entityType) })
    },
  })
}
